import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { getCurrencySettings } from '../services/currencySettingsService';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import AmountInput from './ui/AmountInput';
import SoloTrialExpiryBanner from './SoloTrialExpiryBanner';
import SandboxBanner from './SandboxBanner';
import {
  HomeIcon,
  PlusIcon,
  ClockIcon,
  CheckIcon,
  FileTextIcon,
  Wallet,
  UsersIcon,
  SettingsIcon,
  LogOutIcon,
  UserIcon,
  MenuIcon,
  XIcon,
  MessageSquareIcon,
  HelpCircleIcon,
  TrendingUpIcon,
  BarChartIcon,
  Building2,
  ChevronDown,
  ChevronUp,
  DollarSign,
  InfoIcon,
  Edit3Icon,
  FolderIcon,
  CreditCard as CreditCardIcon
} from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import ImpersonationBanner from './ImpersonationBanner';
import Settings from './Settings';
import CostCenters from './CostCenters';
import PendingApprovals from './PendingApprovals';
import ApprovedAdvances from './ApprovedAdvances';
import AdvanceClosing from './AdvanceClosing';
import AdvanceClosingDetail from './AdvanceClosingDetail';
import ClosingSummary from './ClosingSummary';
import ExpenseReport from './ExpenseReport';
import ClosedAdvances from './ClosedAdvances';
import CurrentAccount from './CurrentAccount';
import AllRequests from './AllRequests';
import FinanceApproval from './FinanceApproval';
import FinanceApprovalDetail from './FinanceApprovalDetail';
import Help from './MyQuestions';
import Documentation from './Documentation';
import MessagesModal from './Messages';
import UserManagement from './UserManagement';
import ComingSoon from './ComingSoon';
// Document Management
import DocumentManagement from './DocumentManagement';
import SoloDocumentsPage from './SoloDocumentsPage';

// Solo Plan pages
import SoloExpenseEntry from './SoloExpenseEntry';
import SoloExpenseList from './SoloExpenseList';
import SoloCardExpenses from './SoloCardExpenses';
import SuppliersDirectory from './SuppliersDirectory';
import SoloDashboardCards from './SoloDashboardCards';
import SoloMockupToggle from './SoloMockupToggle';
import SoloOnboardingTour from './SoloOnboardingTour';

// CRM Pages
import CRMDashboard from '../pages/CRM/Dashboard';
import Leads from '../pages/CRM/Leads';
import Customers from '../pages/CRM/Customers';
import InactiveCustomers from '../pages/CRM/InactiveCustomers';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 🎯 KALICI ÇÖZÜM: Para birimi sembolü için helper fonksiyon
const getCurrencySymbol = (currencyCode) => {
  const symbols = {
    'TRY': '₺',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CHF': 'Fr',
    'AED': 'د.إ',
    'SAR': '﷼',
    'KWD': 'د.ك',
    'QAR': '﷼',
    'BHD': '.د.ب',
    'OMR': '﷼',
    'JOD': 'د.ا',
    'CNY': '¥',
    'INR': '₹',
    'RUB': '₽',
    'BRL': 'R$',
    'MXN': '$',
    'CAD': 'C$',
    'AUD': 'A$',
    'NZD': 'NZ$',
    'SGD': 'S$',
    'HKD': 'HK$',
    'KRW': '₩',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'CZK': 'Kč',
    'HUF': 'Ft',
    'RON': 'lei',
    'BGN': 'лв',
    'HRK': 'kn',
    'ILS': '₪',
    'ZAR': 'R',
    'THB': '฿',
    'MYR': 'RM',
    'IDR': 'Rp',
    'PHP': '₱',
    'VND': '₫',
    'PKR': '₨',
    'BDT': '৳',
    'LKR': '₨',
    'NPR': '₨',
    'AFN': '؋',
    'IRR': '﷼',
    'IQD': 'ع.د',
    'SYP': '£',
    'LBP': 'ل.ل',
    'EGP': '£',
    'MAD': 'د.م.',
    'TND': 'د.ت',
    'DZD': 'د.ج',
    'LYD': 'ل.د'
  };
  return symbols[currencyCode] || currencyCode;
};

const Dashboard = () => {
  const { user, logout, setToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [messagesModalOpen, setMessagesModalOpen] = useState(false);
  
  // CRM menü state
  const [crmMenuOpen, setCrmMenuOpen] = useState(() => {
    const saved = localStorage.getItem('crmMenuOpen');
    return saved ? saved === 'true' : false;
  });
  
  // Save CRM menu state to localStorage
  useEffect(() => {
    localStorage.setItem('crmMenuOpen', crmMenuOpen);
  }, [crmMenuOpen]);

  // Unread message count for the sidebar badge (polled every 60s)
  const [messagesUnread, setMessagesUnread] = useState(0);
  // Yeni mesaj bildirim toast'ı için önceki değerleri hatırla — count artışı
  // yakalanınca conversations'tan gönderen adı + preview çekilir.
  const prevUnreadRef = React.useRef(0);
  const shownMsgKeysRef = React.useRef(new Set()); // duplicate toast önlemi
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const API = process.env.REACT_APP_BACKEND_URL;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API}/api/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        const newCount = d.unread_count || 0;
        if (cancelled) return;
        setMessagesUnread(newCount);
        // Sağ alt köşede bildirim: sadece count arttıysa VE kullanıcı mesajlar
        // sayfasında değilse. 3sn'de otomatik kapanır (kullanıcı kapatırsa
        // daha erken).
        const prev = prevUnreadRef.current;
        prevUnreadRef.current = newCount;
        if (newCount > prev && !window.location.pathname.includes('/messages')) {
          try {
            const cr = await fetch(`${API}/api/messages/conversations`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (cr.ok) {
              const convs = await cr.json();
              // En yeni okunmamış konuşma
              const unread = (Array.isArray(convs) ? convs : [])
                .filter((c) => (c.unread_count || 0) > 0)
                .sort((a, b) => new Date(b.last_message_time || 0) - new Date(a.last_message_time || 0));
              const top = unread[0];
              if (top) {
                const key = `${top.user_id}:${top.last_message_time}`;
                if (!shownMsgKeysRef.current.has(key)) {
                  shownMsgKeysRef.current.add(key);
                  const preview = (top.last_message || '').slice(0, 90);
                  toast.info(`💬 ${top.user_name || 'Yeni mesaj'}`, {
                    description: preview || 'Bir mesaj aldınız',
                    duration: 3000,
                    action: {
                      label: 'Aç',
                      onClick: () => { window.location.href = '/dashboard/messages'; },
                    },
                  });
                }
              }
            }
          } catch (_) { /* silent */ }
        }
      } catch (e) { /* silent */ }
    };
    fetchCount();
    // Bildirimin hızlı yakalanması için 15 saniyede bir kontrol.
    const id = setInterval(fetchCount, 15000);
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.id]);
  // Clear local badge when opening messages page (server flag may catch up later)
  useEffect(() => {
    if (location.pathname === '/dashboard/messages') {
      setMessagesUnread(0);
    }
  }, [location.pathname]);


  // Check if onboarding is needed - redirect to full-page onboarding
  useEffect(() => {
    console.log('🔍 Onboarding Check:', {
      hasUser: !!user,
      hasCompany: !!user?.company,
      companyData: user?.company,
      onboardingCompleted: user?.company?.onboarding_completed,
      shouldRedirect: user?.company && user.company.onboarding_completed === false
    });
    
    // Strict check: only redirect if explicitly false
    if (user?.company && user.company.onboarding_completed === false) {
      console.log('✅ Redirecting to full-page onboarding');
      navigate('/onboarding');
    } else {
      console.log('❌ Not redirecting to onboarding:', {
        reason: !user ? 'No user' : !user.company ? 'No company' : user.company.onboarding_completed !== false ? 'Already completed or undefined' : 'Unknown'
      });
    }
  }, [user, navigate]);

  // Check for impersonation token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const impersonateToken = params.get('impersonate_token');
    
    if (impersonateToken) {
      console.log('Impersonate token found:', impersonateToken.substring(0, 20) + '...');
      
      // Set the impersonation token
      localStorage.setItem('token', impersonateToken);
      
      // Remove token from URL without reload first
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Force reload to apply new token
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 100);
    }
  }, []);

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Çıkış yapıldı');
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownOpen && !event.target.closest('.relative')) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Check if user is loaded
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Check if user has finance approval permissions
  const hasFinancePermissions = () => {
    if (!user?.role?.name) return false;
    return ['Super Admin', 'Süper Admin', 'Admin', 'Finans', 'Muhasebe'].includes(user.role.name);
  };

  // Check if user has admin permissions (for Settings access)
  const hasAdminPermissions = () => {
    if (!user?.role?.name) return false;
    return ['Super Admin', 'Süper Admin', 'Admin'].includes(user.role.name);
  };

  // Base menu items (for all users)
  const baseMenuItems = [
    { path: '/dashboard', name: 'Dashboard', icon: HomeIcon },
    { path: '/dashboard/expenses/new', name: 'Yeni Harcama', icon: PlusIcon, feature: 'expense_entry' },
    { path: '/dashboard/expenses', name: 'Tüm Harcamalarım', icon: FileTextIcon, feature: 'personal_expenses' },
    { path: '/dashboard/card-expenses', name: 'Kart Harcamaları', icon: CreditCardIcon, feature: 'personal_expenses' },
    { path: '/dashboard/all-requests', name: 'Tüm Talepler', icon: FileTextIcon, feature: 'all_requests' },
    { path: '/dashboard/new-request', name: 'Yeni Talep', icon: PlusIcon, feature: 'new_advance_request' },
    { path: '/dashboard/pending', name: 'Onay Bekleyenler', icon: ClockIcon, feature: 'pending_approvals' },
    { path: '/dashboard/approved', name: 'Onaylı Avanslar', icon: CheckIcon, feature: 'approved_advances' },
    { path: '/dashboard/closing', name: 'Avans Kapama', icon: CheckIcon, feature: 'advance_closing' },
    { path: '/dashboard/finance-approval', name: 'Finans Onayı', icon: CheckIcon, feature: 'finance_approval' },
    { path: '/dashboard/closed', name: 'Kapanmış Avanslar', icon: FileTextIcon, feature: 'closed_advances' },
    { path: '/dashboard/current-account', name: 'Cari Hesap', icon: Wallet, feature: 'current_account', hideForSolo: true },
    { path: '/dashboard/suppliers', name: 'İşletmeler', icon: FolderIcon, feature: 'personal_expenses' },
    { path: '/dashboard/documents', name: 'Belgeler', icon: FileTextIcon, feature: 'expense_reporting' },
    { path: '/dashboard/messages', name: 'Mesajlar', icon: MessageSquareIcon, feature: 'messaging' },
    { path: '/dashboard/cost-centers', name: 'Masraf Merkezleri', icon: FolderIcon, feature: 'category_management' },
    // Settings is intentionally NOT feature-gated at the menu level — the
    // Settings page itself enforces role-based access to admin-only sections
    // (Roles/Permissions/User Management). Every plan needs profile + security
    // + language settings access, so the menu item is always visible.
    { path: '/dashboard/settings', name: 'Ayarlar', icon: SettingsIcon },
    { path: '/dashboard/help', name: 'Yardım', icon: HelpCircleIcon },
  ];

  // Helper: does user's plan grant this feature?
  // - undefined/empty feature on item → always allowed
  // - missing plan_features (older clients) → always allowed (no regression)
  // - '*' in plan_features → wildcard (Ultra Admin / master template)
  const planFeatures = Array.isArray(user?.plan_features) ? user.plan_features : null;
  const userHasFeature = (slug) => {
    if (!slug) return true;
    if (!planFeatures) return true;
    if (planFeatures.includes('*')) return true;
    return planFeatures.includes(slug);
  };

  // Ultra Admin Company ID — still used to gate the CRM menu (separate concern).
  const ULTRA_ADMIN_COMPANY_ID = '5fd47ecc-9753-45fb-85d3-5c0abc229df4';
  const isUltraAdminCompany = user?.company_id === ULTRA_ADMIN_COMPANY_ID;

  const menuItemsRaw = baseMenuItems;
  
  // Apply feature gating (hide items whose feature slug is not in the user's plan)
  // + plan-specific hide flags (e.g. Cari Hesap is hidden on Solo because Solo
  // is a spending-tracker only, no advances are taken or closed).
  const isSoloPlanLayout =
    (user?.company?.subscription_plan || '').toLowerCase() === 'solo';
  const menuItems = menuItemsRaw.filter((item) => {
    if (item.hideForSolo && isSoloPlanLayout) return false;
    return userHasFeature(item.feature);
  });
  
  // CRM Menü Yapısı
  const crmMenuItems = [
    { path: '/dashboard/crm', name: 'Dashboard', icon: '📊', exact: true },
    { path: '/dashboard/crm/leads', name: 'Müşteri Adayları', icon: '🎯' },
    { path: '/dashboard/crm/customers', name: 'Müşteriler', icon: '👥' },
    { path: '/dashboard/crm/inactive-customers', name: 'Pasif Müşteriler', icon: '💤' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Impersonation Banner - Shows when Ultra Admin is impersonating */}
      <ImpersonationBanner />

      <div className="min-h-screen bg-gray-50 flex">
        {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)}></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 min-w-64 sidebar transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex flex-col h-full">
          {/* Logo — shows tenant custom logo when the plan has custom_branding
              and the admin has uploaded one, otherwise falls back to the
              platform "Advancecash.io" wordmark. */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            {user?.branding?.logo_data_url ? (
              <img
                src={user.branding.logo_data_url}
                alt={user?.company?.company_name || 'Company logo'}
                className="max-h-10 max-w-[180px] object-contain"
                data-testid="dashboard-logo-custom"
              />
            ) : (
              <h1 className="text-xl font-bold gradient-text" data-testid="dashboard-logo">
                Advancecash.io
              </h1>
            )}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => (
              item.isModal ? (
                <button
                  key={item.path}
                  onClick={() => {
                    setMessagesModalOpen(true);
                    setSidebarOpen(false);
                  }}
                  className="sidebar-link w-full text-left"
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  title={item.name}
                >
                  <item.icon className="sidebar-icon" />
                  <span className="truncate">{item.name}</span>
                </button>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  title={item.name}
                >
                  <item.icon className="sidebar-icon" />
                  <span className="truncate flex-1">{item.name}</span>
                  {item.feature === 'messaging' && messagesUnread > 0 && (
                    <span
                      className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold text-white bg-red-500 rounded-full"
                      data-testid="messages-unread-badge"
                    >
                      {messagesUnread > 99 ? '99+' : messagesUnread}
                    </span>
                  )}
                </Link>
              )
            ))}

            {/* CRM Menü Grubu (Sadece Ultra Admin Company) */}
            {isUltraAdminCompany && (
              <div className="mt-2">
                <button
                  onClick={() => setCrmMenuOpen(!crmMenuOpen)}
                  className="sidebar-link w-full text-left flex items-center justify-between"
                  data-testid="nav-crm"
                >
                  <div className="flex items-center">
                    <span className="sidebar-icon text-xl">📋</span>
                    <span className="truncate ml-2">CRM</span>
                  </div>
                  {crmMenuOpen ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                
                {crmMenuOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    {crmMenuItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`sidebar-link text-sm ${
                          item.exact 
                            ? location.pathname === item.path ? 'active' : ''
                            : location.pathname.startsWith(item.path) && item.path !== '/dashboard/crm' ? 'active' : 
                              location.pathname === item.path ? 'active' : ''
                        }`}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        title={item.name}
                      >
                        <span className="sidebar-icon text-base">{item.icon}</span>
                        <span className="truncate">{item.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

          </nav>

          {/* Logout */}
          <div className="border-t border-gray-200 p-4">
            <button
              onClick={handleLogout}
              className="sidebar-link w-full text-left text-red-600 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOutIcon className="sidebar-icon" />
              Çıkış Yap
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:ml-0 min-w-0">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-3 sm:px-4 gap-2">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 flex-shrink-0"
              data-testid="mobile-menu-btn"
            >
              <MenuIcon className="w-6 h-6" />
            </button>
            
            <div className="flex items-center space-x-4 min-w-0 flex-1">
              <div className="hidden sm:block min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 truncate" data-testid="page-title">
                  {menuItems.find(item => item.path === location.pathname)?.name || 'Dashboard'}
                </h2>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              {/* Trial Warning — urgency-tiered CTA. Colour and copy
                  intensify as expiry approaches so users are nudged early
                  (green) and pressed hard (red/pulse) on the last day. */}
              {user.trial_info && user.trial_info.is_trial && (() => {
                const days = user.trial_info.days_remaining ?? 0;
                const expired = days <= 0;
                // Tier: safe (>7), soft (4–7), warn (2–3), critical (1), expired (0)
                let tier;
                if (expired) tier = 'expired';
                else if (days === 1) tier = 'critical';
                else if (days <= 3) tier = 'warn';
                else if (days <= 7) tier = 'soft';
                else tier = 'safe';

                const styles = {
                  safe:     { box: 'bg-emerald-50 border-emerald-200',
                              text: 'text-emerald-700',
                              btn:  'bg-emerald-600 hover:bg-emerald-700',
                              icon: '🎯', pulse: '' },
                  soft:     { box: 'bg-amber-50 border-amber-200',
                              text: 'text-amber-700',
                              btn:  'bg-amber-600 hover:bg-amber-700',
                              icon: '⏳', pulse: '' },
                  warn:     { box: 'bg-orange-50 border-orange-300',
                              text: 'text-orange-700 font-semibold',
                              btn:  'bg-orange-600 hover:bg-orange-700',
                              icon: '⚠️', pulse: '' },
                  critical: { box: 'bg-red-50 border-red-300',
                              text: 'text-red-700 font-bold',
                              btn:  'bg-red-600 hover:bg-red-700',
                              icon: '🚨', pulse: 'animate-pulse' },
                  expired:  { box: 'bg-red-100 border-red-400',
                              text: 'text-red-800 font-bold',
                              btn:  'bg-red-700 hover:bg-red-800',
                              icon: '🔒', pulse: 'animate-pulse' },
                }[tier];

                const label = expired
                  ? 'Deneme süresi doldu'
                  : days === 1
                    ? 'Son 1 gün!'
                    : `Deneme süresi: ${days} gün kaldı`;

                const btnLabel = tier === 'safe'
                  ? 'Planı Aktifleştir'
                  : tier === 'expired'
                    ? 'Şimdi Aktifleştir'
                    : 'Hemen Yükselt';

                return (
                  <div
                    className={`hidden md:block ${styles.box} border rounded-lg px-3 py-2 text-sm ${styles.pulse}`}
                    data-testid={`trial-warning-${tier}`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className={styles.text} data-testid="trial-days-remaining">
                        <span className="mr-1" aria-hidden="true">{styles.icon}</span>
                        {label}
                      </span>
                      <Link
                        to="/subscription"
                        className={`${styles.btn} text-white px-2 py-1 rounded text-xs transition-colors`}
                        data-testid="trial-upgrade-cta"
                      >
                        {btnLabel}
                      </Link>
                    </div>
                  </div>
                );
              })()}
              
              <div className="hidden sm:block text-sm text-gray-600">
                <span className="font-medium">Plan:</span>
                <Badge variant="outline" className="ml-1" data-testid="header-plan-badge">
                  {(() => {
                    // Plan slug → Turkish label mapping. Applies both to active
                    // subscriptions and to trial users (so a Starter-trial user
                    // sees "Başlangıç Deneme" instead of generic "Ücretsiz Deneme").
                    // If Ultra Admin has customised the plan name in DB, prefer
                    // that (available via `user.plan_name` from /auth/me).
                    const PLAN_LABELS = {
                      solo: 'Solo',
                      starter: 'Başlangıç',
                      professional: 'Profesyonel',
                      enterprise: 'Kurumsal',
                      free_trial: 'Ücretsiz Deneme',
                      trial: 'Ücretsiz Deneme',
                    };
                    const dbName = (user.plan_name && String(user.plan_name).trim()) || '';
                    const slugToLabel = (slug) => {
                      if (!slug) return null;
                      const key = String(slug).toLowerCase();
                      const defaultLabel = PLAN_LABELS[key];
                      // Prefer DB override only when it matches this slug
                      const currentSlug = String(user.plan_slug || user.company?.subscription_plan || '').toLowerCase();
                      if (dbName && key === currentSlug && dbName !== defaultLabel) {
                        return dbName;
                      }
                      if (defaultLabel) return defaultLabel;
                      return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                    };

                    if (user.trial_info?.is_trial) {
                      const trialSlug = user.trial_info.plan;
                      const key = String(trialSlug || '').toLowerCase();
                      // free_trial / trial → just "Ücretsiz Deneme"
                      if (!key || key === 'free_trial' || key === 'trial') {
                        return 'Ücretsiz Deneme';
                      }
                      const label = slugToLabel(trialSlug);
                      return label ? `${label} Deneme` : 'Ücretsiz Deneme';
                    }

                    const planLabel = slugToLabel(user.plan_slug)
                      || slugToLabel(user.company?.subscription_plan)
                      || '—';
                    const cycleRaw = (user.company?.subscription_type || '').toLowerCase();
                    const cycleLabel = cycleRaw === 'yearly'
                      ? 'Yıllık'
                      : cycleRaw === 'monthly'
                        ? 'Aylık'
                        : '';
                    return cycleLabel ? `${planLabel} • ${cycleLabel}` : planLabel;
                  })()}
                </Badge>
              </div>

              {/* Sandbox Mode header badge — only visible when sandbox_mode is on */}
              {user?.sandbox_mode && (
                <div className="text-sm" data-testid="sandbox-header-badge">
                  <Badge
                    variant="outline"
                    className="bg-amber-100 border-amber-400 text-amber-900 font-medium"
                  >
                    🧪 SANDBOX
                  </Badge>
                </div>
              )}

              {/* User Dropdown Menu */}
              <div className="relative border-l border-gray-200 pl-4">
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center space-x-3 text-left hover:bg-gray-50 rounded-lg p-2 transition-colors"
                >
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                    <UserIcon className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.full_name || user?.email || 'Kullanıcı'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.role?.name || 'Admin'}
                    </p>
                    {user?.company?.tenant_number && (
                      <p className="text-xs text-purple-600 font-mono">
                        #{user.company.tenant_number}
                      </p>
                    )}
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${
                      userDropdownOpen ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {userDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.full_name || 'Kullanıcı'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user?.email}
                        </p>
                      </div>
                      
                      <Link
                        to="/dashboard/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserDropdownOpen(false)}
                      >
                        <SettingsIcon className="w-4 h-4 mr-3" />
                        Profil Ayarları
                      </Link>
                      
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOutIcon className="w-4 h-4 mr-3" />
                        Çıkış Yap
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Solo trial expiry banner — visible only when Solo trial has ≤3 days left */}
        <SoloTrialExpiryBanner user={user} />

        {/* Sandbox Mode banner — visible only when user has toggled sandbox_mode on */}
        <SandboxBanner user={user} />

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-y-auto">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="new-request" element={<NewRequest />} />
            <Route path="pending" element={<PendingApprovals />} />
            <Route path="approved" element={<ApprovedAdvances />} />
            <Route path="finance-approval" element={<FinanceApproval />} />
            <Route path="finance-approval/:requestId" element={<FinanceApprovalDetail />} />
            <Route path="my-questions" element={<Help />} />
            <Route path="closing" element={<AdvanceClosing />} />
            <Route path="closing/:requestId" element={<AdvanceClosingDetail />} />
            <Route path="closing-summary/:requestId" element={<ClosingSummary />} />
            <Route path="expense-report/:requestId" element={<ExpenseReport />} />
            <Route path="closed" element={<ClosedAdvances />} />
            <Route path="current-account" element={<CurrentAccount />} />
            <Route path="suppliers" element={<SuppliersDirectory />} />
            <Route path="all-requests" element={<AllRequests />} />
            <Route path="help" element={<Help />} />
            <Route path="documentation" element={<Documentation />} />
            <Route path="settings" element={<Settings />} />
            <Route path="cost-centers" element={<CostCenters />} />
            
            {/* Belge Yönetimi — Solo için özel sadeleştirilmiş sayfa
                (yalnızca harcamalara ekli fişler + gün/ay/kategori
                filtreleri). Diğer planlar mevcut multi-tenant görünümü
                korur. */}
            <Route
              path="documents"
              element={isSoloPlanLayout ? <SoloDocumentsPage /> : <DocumentManagement />}
            />
            <Route path="messages" element={<MessagesModal isOpen={true} mode="page" onClose={() => {}} />} />
            <Route path="expenses/new" element={<SoloExpenseEntry />} />
            <Route path="expenses" element={<SoloExpenseList />} />
            <Route path="card-expenses" element={<SoloCardExpenses />} />

            {/* CRM Routes (Sadece Ultra Admin Company) */}
            {isUltraAdminCompany && (
              <Route path="crm">
                <Route index element={<CRMDashboard />} />
                <Route path="leads" element={<Leads />} />
                <Route path="customers" element={<Customers />} />
                <Route path="inactive-customers" element={<InactiveCustomers />} />
              </Route>
            )}
            
            <Route path="*" element={<ComingSoon />} />
          </Routes>
        </main>
        
        {/* Messages Modal */}
        <MessagesModal isOpen={messagesModalOpen} onClose={() => setMessagesModalOpen(false)} />
      </div>
      </div>
    </>
  );
};

// Dashboard Home Component
const DashboardHome = () => {
  const { user } = useAuth();
  // Feature gating helper (mirror of the one defined in parent Dashboard).
  // Required here because DashboardHome is a sibling component and cannot see
  // the helper through closure. Same semantics: missing plan_features → allowed,
  // '*' wildcard (Ultra Admin / master template) → allowed.
  const planFeatures = Array.isArray(user?.plan_features) ? user.plan_features : null;
  const userHasFeature = (slug) => {
    if (!slug) return true;
    if (!planFeatures) return true;
    if (planFeatures.includes('*')) return true;
    return planFeatures.includes(slug);
  };
  const [dashboardStats, setDashboardStats] = useState({
    talep_edilen: 0,
    onaylanan: 0,
    odenen: 0,
    kapatilan: 0,
    bakiye: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [expensesByProject, setExpensesByProject] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(true);
  
  // View mode: "personal" or "company"
  const [viewMode, setViewMode] = useState('personal');
  
  // Check if user can see company view (toggle hidden for Solo plan since
  // Solo is a single-user tier — "Şirket Geneli" view is meaningless).
  const canSeeCompanyView = () => {
    const plan = (user?.company?.subscription_plan || '').toLowerCase();
    if (plan === 'solo') return false;
    const roleName = user?.role?.name;
    return ['Super Admin', 'Süper Admin', 'Admin', 'Muhasebe'].includes(roleName);
  };

  // Solo-plan detection used to swap the welcome copy + quick-action card
  // away from the advance-flow defaults toward Solo-relevant shortcuts.
  const isSoloPlan = (user?.company?.subscription_plan || '').toLowerCase() === 'solo';
  
  // Currency filter states (like AllRequests)
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [baseCurrency, setBaseCurrency] = useState('TRY');
  const [isBaseCurrencyView, setIsBaseCurrencyView] = useState(true);
  const [allRequests, setAllRequests] = useState([]); // Store all requests for filtering

  // Email delivery health for current user — used to show a warning banner
  // when password-reset / notification mails are bouncing or blocked at the
  // recipient's mail server (SendGrid webhook feeds this).
  const [emailDeliveryStatus, setEmailDeliveryStatus] = useState(null);
  const [emailWarnDismissed, setEmailWarnDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('email_warn_dismissed') === '1';
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/me/email-delivery-status`,
        );
        if (!cancelled) setEmailDeliveryStatus(res.data || null);
      } catch (_) {
        // silent fail — endpoint may be temporarily unavailable
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismissEmailWarning = () => {
    try { sessionStorage.setItem('email_warn_dismissed', '1'); } catch (_) { /* ignore */ }
    setEmailWarnDismissed(true);
  };

  // Fetch available currencies
  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/advance-requests/currencies`);
      setAvailableCurrencies(response.data.all_currencies || []);
      setBaseCurrency(response.data.base_currency || 'TRY');
      setSelectedCurrency(response.data.base_currency || 'TRY');
      setIsBaseCurrencyView(true);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };
  
  // Fetch all requests for filtering with view_mode parameter
  const fetchAllRequests = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/advance-requests/all?view_mode=${viewMode}`);
      setAllRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  // Fetch dashboard statistics
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats?view_mode=${viewMode}`);
        console.log('=== Dashboard Stats Response ===');
        console.log('Full Response:', response.data);
        console.log('Manager Info:', response.data.manager_info);
        setDashboardStats(response.data);
      } catch (error) {
        console.error('Dashboard stats fetch error:', error);
        toast.error('İstatistikler yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCurrencies();
    fetchAllRequests();
    fetchDashboardStats();
  }, [viewMode]); // Re-fetch when viewMode changes

  // Fetch recent activities
  useEffect(() => {
    const fetchRecentActivities = async () => {
      try {
        setActivitiesLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/activities/recent?limit=5`);
        setRecentActivities(response.data.activities || []);
      } catch (error) {
        console.error('Failed to fetch recent activities:', error);
        setRecentActivities([]);
      } finally {
        setActivitiesLoading(false);
      }
    };

    if (user) {
      fetchRecentActivities();
    }
  }, [user]);

  // Fetch expenses by project
  useEffect(() => {
    const fetchExpensesByProject = async () => {
      try {
        setExpensesLoading(true);
        
        // Fetch all expenses
        const expensesResponse = await axios.get(`${API}/advance-expenses/all`);
        const expenses = expensesResponse.data || [];
        
        // Fetch projects
        const projectsResponse = await axios.get(`${API}/cost-centers/projects`);
        const projects = projectsResponse.data || [];
        
        // Group expenses by project
        const projectExpenseMap = {};
        
        expenses.forEach(expense => {
          if (expense.cost_center_type === 'project' && expense.cost_center_id) {
            const projectId = expense.cost_center_id;
            if (!projectExpenseMap[projectId]) {
              projectExpenseMap[projectId] = {
                total: 0,
                count: 0
              };
            }
            projectExpenseMap[projectId].total += parseFloat(expense.amount || 0);
            projectExpenseMap[projectId].count += 1;
          }
        });
        
        // Build chart data
        const chartData = projects
          .map(project => ({
            name: project.project_name,
            total: projectExpenseMap[project.id]?.total || 0,
            count: projectExpenseMap[project.id]?.count || 0
          }))
          .filter(item => item.total > 0)
          .sort((a, b) => b.total - a.total)
          .slice(0, 10); // Top 10 projects
        
        setExpensesByProject(chartData);
      } catch (error) {
        console.error('Failed to fetch expenses by project:', error);
        setExpensesByProject([]);
      } finally {
        setExpensesLoading(false);
      }
    };

    if (user) {
      fetchExpensesByProject();
    }
  }, [user]);
  
  // Calculate filtered stats based on selected currency
  const getFilteredStats = () => {
    // Always use allRequests for counting
    if (!allRequests.length) {
      // If no requests loaded yet, return default empty stats
      return {
        talep_edilen: 0,
        onaylanan: 0,
        odenen: 0,
        kapama_bekleyen: 0,
        kapatilan: 0,
        bakiye: 0,
        total_count: 0,
        status_counts: {
          pending: 0,
          approved: 0,
          rejected: 0,
          finance_approval: 0,
          closed: 0
        }
      };
    }
    
    // Filter requests based on currency selection
    let filtered = allRequests;
    if (!isBaseCurrencyView && selectedCurrency) {
      filtered = allRequests.filter(req => req.currency === selectedCurrency);
    }
    
    // Calculate stats
    const talep_edilen = isBaseCurrencyView
      ? filtered.reduce((sum, req) => sum + (req.converted_amount || 0), 0)
      : filtered.reduce((sum, req) => sum + (req.amount || 0), 0);
      
    const onaylananReqs = filtered.filter(req => ['approved', 'paid', 'finance_pending', 'closed'].includes(req.status));
    const onaylanan = isBaseCurrencyView
      ? onaylananReqs.reduce((sum, req) => sum + (req.converted_approved_amount || req.converted_amount || 0), 0)
      : onaylananReqs.reduce((sum, req) => sum + (req.approved_amount || req.amount || 0), 0);
      
    const odenenReqs = filtered.filter(req => ['paid', 'finance_pending', 'closed'].includes(req.status));
    const odenen = isBaseCurrencyView
      ? odenenReqs.reduce((sum, req) => sum + (req.converted_approved_amount || req.converted_amount || 0), 0)
      : odenenReqs.reduce((sum, req) => sum + (req.approved_amount || req.amount || 0), 0);
      
    // Kapama Bekleyen - 'paid' durumundaki avanslar (ödendi, kapama bekleniyor)
    // 'finance_pending' = kapama belgeleri sunuldu, finans inceliyor (ayrı KPI)
    const kapamaBekleyenReqs = filtered.filter(req => req.status === 'paid');
    const kapama_bekleyen = isBaseCurrencyView
      ? kapamaBekleyenReqs.reduce((sum, req) => sum + (req.converted_approved_amount || req.converted_amount || 0), 0)
      : kapamaBekleyenReqs.reduce((sum, req) => sum + (req.approved_amount || req.amount || 0), 0);
      
    const kapatilanReqs = filtered.filter(req => req.status === 'closed');
    const kapatilan = isBaseCurrencyView
      ? kapatilanReqs.reduce((sum, req) => sum + (req.converted_closed_amount || 0), 0)
      : kapatilanReqs.reduce((sum, req) => sum + (req.converted_closed_amount || 0), 0);
      
    // Bakiye = kapatilan - odenen (negative if user owes company)
    const bakiye = kapatilan - odenen;
    
    // Count statistics - FIXED: Added rejected, paid (kapama bekliyor), and total_count
    const status_counts = {
      pending: filtered.filter(req => req.status === 'pending').length,
      approved: filtered.filter(req => req.status === 'approved').length,
      rejected: filtered.filter(req => req.status === 'rejected').length,
      paid: filtered.filter(req => req.status === 'paid').length,
      finance_approval: filtered.filter(req => req.status === 'finance_pending').length,
      closed: filtered.filter(req => req.status === 'closed').length
    };
    
    // Total count - ALL requests (including rejected)
    const total_count = filtered.length;
    
    return {
      talep_edilen,
      onaylanan,
      odenen,
      kapama_bekleyen,
      kapatilan,
      bakiye,
      total_count,
      status_counts
    };
  };
  
  const filteredStats = getFilteredStats();

  // Format currency for Turkish locale
  const formatCurrency = (amount, currency = null) => {
    const currencyToUse = currency || selectedCurrency || 'TRY';
    const numAmount = Number(amount) || 0;
    const formatted = numAmount.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    // Use getCurrencySymbol helper for ALL currencies
    return `${getCurrencySymbol(currencyToUse)}${formatted}`;
  };

  const stats = [
    { name: 'Onaylanan', value: formatCurrency(filteredStats.onaylanan), color: 'bg-green-500', icon: '✅', textColor: 'text-gray-900' },
    { name: 'Ödenen', value: formatCurrency(filteredStats.odenen), color: 'bg-purple-500', icon: '💰', textColor: 'text-gray-900' },
    { name: 'Kapama Bekleyen', value: formatCurrency(filteredStats.kapama_bekleyen), color: 'bg-orange-500', icon: '⏳', textColor: 'text-gray-900' },
    { name: 'Kapatılan', value: formatCurrency(filteredStats.kapatilan), color: 'bg-teal-500', icon: '🔒', textColor: 'text-gray-900' },
    { 
      name: 'Bakiye', 
      value: (filteredStats.bakiye < 0 ? '-' : '+') + formatCurrency(Math.abs(filteredStats.bakiye)), 
      color: 'bg-gray-100',
      textColor: filteredStats.bakiye < 0 ? 'text-red-600' : 'text-green-600',
      icon: '⚖️',
      message: filteredStats.bakiye < 0 ? 'Şirkete Borçlusunuz' : (filteredStats.bakiye > 0 ? 'Şirketten Alacaklısınız' : ''),
      messageColor: filteredStats.bakiye < 0 ? 'text-red-600' : 'text-green-600'
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* E-posta Teslimat Uyarı Bandı — sadece son maillerden biri reddedildiyse */}
      {emailDeliveryStatus?.is_failing && !emailWarnDismissed && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 shadow-sm"
          data-testid="email-delivery-warning"
          role="alert"
        >
          <div className="shrink-0 mt-0.5 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
            <span className="text-amber-700 text-lg">⚠️</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              E-posta teslimat sorunu tespit ettik
            </p>
            <p className="text-sm text-amber-800 mt-0.5">
              Adresinize (<span className="font-medium">{emailDeliveryStatus.email}</span>) gönderdiğimiz son mail iletilemedi
              {emailDeliveryStatus.last_event ? ` (${emailDeliveryStatus.last_event})` : ''}.
              {emailDeliveryStatus.last_reason
                ? ` Sebep: ${emailDeliveryStatus.last_reason.slice(0, 160)}`
                : ' Posta kutunuzun dolu olmadığından, spam klasörünüzü kontrol etmenizden ve bizim domainimizi (advancecash.io) güvenli göndericiler listesine eklemenizden emin olun.'}
            </p>
          </div>
          <button
            type="button"
            onClick={dismissEmailWarning}
            className="shrink-0 text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded px-2 py-1 text-sm"
            data-testid="email-delivery-warning-dismiss"
            aria-label="Uyarıyı kapat"
          >
            ✕
          </button>
        </div>
      )}

      {/* Welcome Message — hero banner. `brand-hero-gradient` gets overridden
          by tenant primary+secondary colors when custom_branding is active. */}
      <div className="brand-hero-gradient rounded-lg p-4 sm:p-6 text-white">
        <div className="flex items-start justify-between gap-3 sm:gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2 break-words">
              Hoş geldiniz, {user?.full_name || user?.user?.full_name || 'Kullanıcı'}!
            </h1>
            <p className="text-teal-100 text-sm sm:text-base">
              {isSoloPlan
                ? 'Tüm harcamalarınız, işletmeleriniz ve cari hesabınız tek panelde.'
                : `${user?.company?.name || user?.company_name || 'Şirketiniz'} için avans yönetim sisteminiz hazır.`}
            </p>
          </div>

          {/* Solo mockup toggle — welcome banner'ın sağında kompakt kart */}
          {isSoloPlan && (
            <div className="flex-shrink-0 w-full sm:w-auto" data-testid="solo-mockup-toggle-wrapper">
              <SoloMockupToggle variant="inline" theme="dark" />
            </div>
          )}

          {/* Manager Info - Right Side (non-solo plans) */}
          {dashboardStats?.manager_info && !isSoloPlan && (
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 w-full sm:w-auto sm:min-w-[250px]">
              <div className="text-xs text-white/80 mb-1">👤 Yöneticiniz</div>
              <div className="font-bold text-lg text-white">{dashboardStats.manager_info.full_name}</div>
              <div className="text-sm text-white/90 mt-1">{dashboardStats.manager_info.position}</div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">
            {isSoloPlan
              ? '📊 Harcama Özetim'
              : viewMode === 'personal' ? '📊 Kişisel İstatistiklerim' : '📊 Şirket Geneli İstatistikler'}
          </h2>
          
          {/* View Mode Toggle - Only for admin/muhasebe roles */}
          {canSeeCompanyView() && (
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('personal')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'personal'
                    ? 'bg-white text-teal-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Kişisel
              </button>
              <button
                onClick={() => setViewMode('company')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'company'
                    ? 'bg-white text-teal-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Şirket Geneli
              </button>
            </div>
          )}
        </div>
        
        {/* Currency Display - Read Only */}
        <div className="px-4 py-2 bg-teal-50 border border-teal-200 rounded-md text-sm font-semibold text-teal-800 flex items-center space-x-2">
          <span className="text-teal-600">💰</span>
          <span>{baseCurrency} (Temel Para Birimi)</span>
        </div>
      </div>

      {/* Status Count Cards — Solo plan kayıt akışı kullanmadığı için
          tamamen gizlenir. Diğer planlarda her kart kendi feature-gating'i ile
          görünür. */}
      {!isSoloPlan && (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="dashboard-kpi-row">
        {/* 1. Toplam Talepler */}
        <Card className="p-4 text-center hover:shadow-md transition-shadow" data-testid="kpi-toplam">
          <div className="text-sm font-medium text-gray-600 mb-1">Toplam Talepler</div>
          <div className="text-3xl font-bold text-gray-900">
            {loading ? '...' : filteredStats.total_count || 0}
          </div>
        </Card>
        
        {/* 2. Onay Bekliyor */}
        {userHasFeature('pending_approvals') && (
        <Card className="p-4 text-center hover:shadow-md transition-shadow" data-testid="kpi-onay-bekliyor">
          <div className="text-sm font-medium text-gray-600 mb-1">Onay Bekliyor</div>
          <div className="text-3xl font-bold text-yellow-600">
            {loading ? '...' : (filteredStats.status_counts?.pending || 0)}
          </div>
        </Card>
        )}
        
        {/* 3. Onaylandı */}
        {userHasFeature('approved_advances') && (
        <Card className="p-4 text-center hover:shadow-md transition-shadow" data-testid="kpi-onaylandi">
          <div className="text-sm font-medium text-gray-600 mb-1">Onaylandı</div>
          <div className="text-3xl font-bold text-green-600">
            {loading ? '...' : (filteredStats.status_counts?.approved || 0)}
          </div>
        </Card>
        )}
        
        {/* 4. Reddedildi */}
        {userHasFeature('pending_approvals') && (
        <Card className="p-4 text-center hover:shadow-md transition-shadow" data-testid="kpi-reddedildi">
          <div className="text-sm font-medium text-gray-600 mb-1">Reddedildi</div>
          <div className="text-3xl font-bold text-red-600">
            {loading ? '...' : (filteredStats.status_counts?.rejected || 0)}
          </div>
        </Card>
        )}
        
        {/* 5. Kapama Bekliyor (paid - money paid out, awaiting closing) */}
        {userHasFeature('advance_closing') && (
        <Card className="p-4 text-center hover:shadow-md transition-shadow border-amber-200" data-testid="kpi-kapama-bekliyor">
          <div className="text-sm font-medium text-amber-700 mb-1">Kapama Bekliyor</div>
          <div className="text-3xl font-bold text-amber-600">
            {loading ? '...' : (filteredStats.status_counts?.paid || 0)}
          </div>
        </Card>
        )}
        
        {/* 6. Finans Onayında */}
        {userHasFeature('finance_approval') && (
        <Card className="p-4 text-center hover:shadow-md transition-shadow" data-testid="kpi-finans-onayinda">
          <div className="text-sm font-medium text-gray-600 mb-1">Finans Onayında</div>
          <div className="text-3xl font-bold text-orange-600">
            {loading ? '...' : (filteredStats.status_counts?.finance_approval || 0)}
          </div>
        </Card>
        )}
        
        {/* 7. Kapandı */}
        {userHasFeature('closed_advances') && (
        <Card className="p-4 text-center hover:shadow-md transition-shadow" data-testid="kpi-kapandi">
          <div className="text-sm font-medium text-gray-600 mb-1">Kapandı</div>
          <div className="text-3xl font-bold text-gray-600">
            {loading ? '...' : (filteredStats.status_counts?.closed || 0)}
          </div>
        </Card>
        )}
      </div>
      )}

      {/* Amount Stats Grid — solo plan doesn't use advance flow, so the
          advance amount summary is hidden. Solo users get their numbers from
          Cari Hesap + İşletmeler pages. */}
      {!isSoloPlan && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {loading ? (
          // Loading skeleton
          Array.from({length: 5}).map((_, index) => (
            <Card key={index} className="p-4 animate-pulse">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gray-300 rounded-lg mr-4"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded mb-2"></div>
                  <div className="h-6 bg-gray-300 rounded"></div>
                </div>
              </div>
            </Card>
          ))
        ) : (
          stats.map((stat, index) => (
            <Card key={index} className="p-4 hover:shadow-md transition-shadow" data-testid={`stat-${stat.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center">
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center text-white text-xl font-bold mr-4`}>
                  {stat.icon}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-600 font-medium whitespace-nowrap">{stat.name}</p>
                  <p className={`text-base font-bold ${stat.textColor || 'text-gray-900'}`}>{stat.value}</p>
                  {stat.message && (
                    <p className={`text-xs font-medium mt-1 ${stat.messageColor}`}>{stat.message}</p>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
      )}

      {/* Solo-plan dashboard cards — Son Harcamalarım, İşletmelere Göre
          Dağılım, Aylık Solo Trendi + KPI strip. Replaces the team-flavoured
          advance grids that are hidden above for Solo. */}
      {isSoloPlan && <SoloDashboardCards />}

      {/* Solo onboarding turu — yeni kullanıcı ilk kez Dashboard'a
          geldiğinde 4 adımlı highlight tour çalışır (bir kez, localStorage
          bayrağı ile). */}
      {isSoloPlan && <SoloOnboardingTour isSoloPlan={true} />}

      {/* Recent Activity */}
      {!isSoloPlan && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Son Aktiviteler</h3>
          <div className="space-y-4">
            {activitiesLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Yükleniyor...</p>
              </div>
            ) : recentActivities.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquareIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-600 font-medium mb-1">
                  Henüz gerçekleştirilmiş bir aktiviteniz yoktur
                </p>
                <p className="text-xs text-gray-500">
                  Yapacağınız son işlemler burada sergilenecektir
                </p>
              </div>
            ) : (
              recentActivities.map((activity, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    activity.type === 'new' || activity.type === 'created' ? 'bg-blue-500' :
                    activity.type === 'approved' || activity.type === 'success' ? 'bg-green-500' :
                    activity.type === 'rejected' || activity.type === 'cancelled' ? 'bg-red-500' :
                    'bg-gray-500'
                  }`}></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{activity.user_name || 'Kullanıcı'}</span> {activity.description || activity.action}
                    </p>
                    <p className="text-xs text-gray-500">{activity.time || activity.created_at}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Hızlı İşlemler</h3>
          {isSoloPlan ? (
            <div className="grid grid-cols-2 gap-4" data-testid="quick-actions-solo">
              <Link to="/dashboard/expenses/new">
                <Button className="btn-primary w-full h-20 flex-col" data-testid="quick-new-expense">
                  <PlusIcon className="w-6 h-6 mb-1" />
                  <span>Yeni Harcama</span>
                </Button>
              </Link>
              <Link to="/dashboard/suppliers">
                <Button variant="outline" className="w-full h-20 flex-col border-2 hover:bg-gray-50" data-testid="quick-suppliers">
                  <FolderIcon className="w-6 h-6 mb-1" />
                  <span>İşletmeler</span>
                </Button>
              </Link>
              <Link to="/dashboard/current-account">
                <Button variant="outline" className="w-full h-20 flex-col border-2 hover:bg-gray-50" data-testid="quick-current-account">
                  <Wallet className="w-6 h-6 mb-1" />
                  <span>Cari Hesap</span>
                </Button>
              </Link>
              <Link to="/dashboard/settings">
                <Button variant="outline" className="w-full h-20 flex-col border-2 hover:bg-gray-50" data-testid="quick-settings">
                  <SettingsIcon className="w-6 h-6 mb-1" />
                  <span>Ayarlar</span>
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <Link to="/dashboard/new-request">
                <Button className="btn-primary w-full h-20 flex-col" data-testid="quick-new-request">
                  <PlusIcon className="w-6 h-6 mb-1" />
                  <span>Yeni Talep</span>
                </Button>
              </Link>
              <Link to="/dashboard/pending">
                <Button variant="outline" className="w-full h-20 flex-col border-2 hover:bg-gray-50">
                  <ClockIcon className="w-6 h-6 mb-1" />
                  <span>Onaylar</span>
                </Button>
              </Link>
              <Link to="/dashboard/users">
                <Button variant="outline" className="w-full h-20 flex-col border-2 hover:bg-gray-50">
                  <UsersIcon className="w-6 h-6 mb-1" />
                  <span>Kullanıcılar</span>
                </Button>
              </Link>
              <Link to="/dashboard/settings">
                <Button variant="outline" className="w-full h-20 flex-col border-2 hover:bg-gray-50">
                  <SettingsIcon className="w-6 h-6 mb-1" />
                  <span>Ayarlar</span>
                </Button>
              </Link>
            </div>
          )}
        </Card>
      </div>
      )}

      {/* Charts Section */}
      {!isSoloPlan && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expenses by Project Chart */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <BarChartIcon className="w-5 h-5 mr-2 text-teal-600" />
              Projelere Göre Giderler
            </h3>
            <span className="text-xs text-gray-500">Top 10 Proje</span>
          </div>
          {expensesLoading ? (
            <div className="flex items-center justify-center h-80">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : expensesByProject.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-80 text-gray-400">
              <BarChartIcon className="w-16 h-16 mb-3" />
              <p className="text-sm font-medium">Henüz proje bazlı gider bulunmuyor</p>
              <p className="text-xs text-gray-500 mt-1">Harcamalarınıza proje atadıkça burada görünecektir</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={expensesByProject} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={100}
                  tick={{ fontSize: 11 }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'total') return [`${value.toFixed(2)} ${baseCurrency}`, 'Toplam Tutar'];
                    if (name === 'count') return [value, 'Harcama Sayısı'];
                    return value;
                  }}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  formatter={(value) => {
                    if (value === 'total') return 'Toplam Tutar';
                    if (value === 'count') return 'Harcama Sayısı';
                    return value;
                  }}
                />
                <Bar dataKey="total" fill="#0d9488" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Monthly Trend Chart Placeholder */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <TrendingUpIcon className="w-5 h-5 mr-2 text-purple-600" />
              Aylık Harcama Trendi
            </h3>
            <span className="text-xs text-gray-500">Son 6 Ay</span>
          </div>
          <div className="flex flex-col items-center justify-center h-80 text-gray-400">
            <TrendingUpIcon className="w-16 h-16 mb-3" />
            <p className="text-sm font-medium">Aylık trend grafiği yakında eklenecek</p>
            <p className="text-xs text-gray-500 mt-1">Harcamalarınızın aylık dağılımını görebileceksiniz</p>
          </div>
        </Card>
      </div>
      )}
    </div>
  );
};

// New Request Component
const NewRequest = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    amount: '',
    currency: '', // Empty initially - will be set from user settings
    category: '',
    project: '',
    description: '',
    request_date: new Date().toISOString().split('T')[0],
    closure_type: '',
    closure_days: '',
    closure_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  // Pick category display name based on user's preferred language
  const userLang = user?.preferred_language || 'tr';
  const getCategoryDisplayName = (cat) => {
    if (!cat) return '';
    const isEnglish = (userLang || 'tr').toLowerCase().startsWith('en');
    if (isEnglish && cat.name_en) return cat.name_en;
    return cat.name || cat.name_en || '';
  };
  
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [showAddProject, setShowAddProject] = useState(false);
  const [newProjectData, setNewProjectData] = useState({ name: '', description: '' });
  const [addingProject, setAddingProject] = useState(false);
  
  // Currencies state
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  
  // User's allowed currencies from new API
  const [userAllowedCurrencies, setUserAllowedCurrencies] = useState([]);
  
  // Base currency state
  const [baseCurrency, setBaseCurrency] = useState(null);
  const [baseCurrencyChecked, setBaseCurrencyChecked] = useState(false);
  
  // AmountInput component handles formatting now
  
  // Advance rules state
  const [advanceRules, setAdvanceRules] = useState(null);
  const [showMediumModal, setShowMediumModal] = useState(false);
  const [showExtendedModal, setShowExtendedModal] = useState(false);
  const [advanceUsage, setAdvanceUsage] = useState(null);
  const [loadingAdvanceData, setLoadingAdvanceData] = useState(false);
  
  // Advance eligibility state
  const [eligibility, setEligibility] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(true);

  // Limit info modal & limit edit modal state
  const [showLimitInfoModal, setShowLimitInfoModal] = useState(false);
  const [hideLimitInfo, setHideLimitInfo] = useState(
    () => localStorage.getItem('hide_limit_info_icon') === '1'
  );
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showLimitEditModal, setShowLimitEditModal] = useState(false);
  const [limitForm, setLimitForm] = useState({
    max_open_advances: '',
    max_total_amount: ''
  });
  const [savingLimit, setSavingLimit] = useState(false);
  
  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({
    title: '',
    message: '',
    details: {}
  });

  // Check if user has admin permissions
  const hasAdminPermissions = () => {
    if (!user?.role) return false;
    return user.role.name === 'Süper Admin' || user.role.name === 'Admin' || user.role.permissions.includes('*');
  };

  // Handlers for the "Açık Avans Limiti" info icon + limit edit
  const handleCloseLimitInfo = () => {
    if (dontShowAgain) {
      localStorage.setItem('hide_limit_info_icon', '1');
      setHideLimitInfo(true);
    }
    setShowLimitInfoModal(false);
  };

  const openLimitEditModal = () => {
    setLimitForm({
      max_open_advances: eligibility?.max_open_advances ?? '',
      max_total_amount:
        eligibility?.max_total_amount_base ?? eligibility?.max_total_amount_usd ?? ''
    });
    setShowLimitEditModal(true);
  };

  const handleSaveLimit = async () => {
    const maxOpen = parseInt(limitForm.max_open_advances, 10);
    const maxTotal = parseFloat(limitForm.max_total_amount);
    if (!maxOpen || maxOpen < 1) {
      toast.error('Maksimum açık avans sayısı 1 veya daha büyük olmalıdır');
      return;
    }
    if (!maxTotal || maxTotal <= 0) {
      toast.error('Maksimum toplam tutar 0 dan büyük olmalıdır');
      return;
    }
    try {
      setSavingLimit(true);
      await axios.put(`${API}/advance-rules`, {
        max_open_advances: maxOpen,
        max_total_amount: maxTotal
      });
      toast.success('Limit başarıyla güncellendi');
      setShowLimitEditModal(false);
      fetchEligibility();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Limit güncellenemedi');
    } finally {
      setSavingLimit(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Kategoriler yüklenirken hata oluştu');
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch projects
  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await axios.get(`${API}/cost-centers/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Projeler yüklenirken hata oluştu');
    } finally {
      setLoadingProjects(false);
    }
  };

  // Fetch currencies
  const fetchCurrencies = async () => {
    try {
      setLoadingCurrencies(true);
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data);
      
      // Load user's currency settings FIRST
      let userBaseCurrency = null;
      if (user?.id) {
        try {
          const token = localStorage.getItem('token');
          const settings = await getCurrencySettings(user.id, token);
          console.log('📖 User currency settings:', settings);
          setUserAllowedCurrencies(settings.allowedCurrencies || []);
          userBaseCurrency = settings.baseCurrency;
        } catch (error) {
          console.error('Error fetching user currency settings:', error);
          setUserAllowedCurrencies([]);
        }
      }
      
      // Set currency: user base > TRY fallback
      if (userBaseCurrency) {
        setFormData(prev => ({ ...prev, currency: userBaseCurrency }));
        console.log('✅ Set currency to user base:', userBaseCurrency);
      } else if (response.data.length > 0) {
        const defaultCurrency = response.data.find(c => c.code === 'TRY') || response.data[0];
        setFormData(prev => ({ ...prev, currency: defaultCurrency.code }));
        console.log('⚠️ Fallback to default currency:', defaultCurrency.code);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      // Fallback to default currencies if API fails
      setCurrencies([
        { code: 'TRY', symbol: '₺' },
        { code: 'USD', symbol: '$' },
        { code: 'EUR', symbol: '€' }
      ]);
      setUserAllowedCurrencies([]);
    } finally {
      setLoadingCurrencies(false);
    }
  };

  // Fetch base currency
  const fetchBaseCurrency = async () => {
    try {
      const response = await axios.get(`${API}/company/base-currency`);
      setBaseCurrency(response.data.base_currency);
      setBaseCurrencyChecked(true);
    } catch (error) {
      console.error('Error fetching base currency:', error);
      setBaseCurrency(null);
      setBaseCurrencyChecked(true);
    }
  };

  // Fetch advance rules and usage
  const fetchAdvanceRules = async () => {
    try {
      const rulesResponse = await axios.get(`${API}/advance-rules`);
      setAdvanceRules(rulesResponse.data);
      
      const usageResponse = await axios.get(`${API}/advance-usage`);
      setAdvanceUsage(usageResponse.data);
    } catch (error) {
      console.error('Error fetching advance data:', error);
    }
  };

  // Fetch advance eligibility
  const fetchEligibility = async () => {
    try {
      setLoadingEligibility(true);
      const response = await axios.get(`${API}/advance-eligibility`);
      setEligibility(response.data);
    } catch (error) {
      console.error('Error fetching eligibility:', error);
      toast.error('Avans uygunluk bilgileri alınamadı');
    } finally {
      setLoadingEligibility(false);
    }
  };

  React.useEffect(() => {
    fetchCategories();
    fetchProjects();
    fetchCurrencies();
    fetchBaseCurrency();
    fetchAdvanceRules();
    fetchEligibility();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if base currency is set
    if (!baseCurrency) {
      toast.error(
        'Lütfen önce temel para biriminizi ayarlayın',
        {
          description: 'Ayarlar → Kütüphane → Para Birimleri sayfasından temel para biriminizi seçebilirsiniz.',
          duration: 5000
        }
      );
      
      // Navigate to currency settings after 2 seconds
      setTimeout(() => {
        navigate('/dashboard/settings', { state: { openTab: 'library', openSubTab: 'currencies' } });
      }, 2000);
      
      return;
    }
    
    setLoading(true);
    
    try {
      const requestData = {
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        category: formData.category,
        project: formData.project,
        description: formData.description,
        closure_days: parseInt(formData.closure_days)
      };
      
      await axios.post(`${API}/advance-requests`, requestData);
      
      toast.success('Avans talebi başarıyla oluşturuldu!');
      
      // Form'u temizle
      setFormData({
        amount: '',
        currency: 'TRY',
        category: '',
        project: '',
        description: '',
        request_date: new Date().toISOString().split('T')[0],
        closure_type: '',
        closure_days: '',
        closure_date: ''
      });
      
      // Eligibility'yi yenile
      fetchEligibility();
      
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Avans talebi oluşturulurken hata oluştu';
      
      // Check if it's a limit exceeded error
      if (errorDetail.includes('toplam izin verilen avans limitini') || errorDetail.includes('aşmaktadır')) {
        // Parse error message for structured display
        const amountMatch = errorDetail.match(/(\d+[\d,]*\.?\d*)\s*TRY/g);
        const details = {
          requestedAmount: amountMatch?.[0] || '',
          maxLimit: amountMatch?.[1] || '',
          currentOpen: errorDetail.match(/Mevcut açık avanslarınız:\s*([\d,]+\.?\d*\s*TRY)/)?.[1] || '',
          remainingLimit: errorDetail.match(/Kalan limit:\s*([\d,]+\.?\d*\s*TRY)/)?.[1] || ''
        };
        
        setErrorModalData({
          title: 'Avans Limit Aşımı',
          message: errorDetail,
          details: details
        });
        setShowErrorModal(true);
      } else {
        // For other errors, use toast
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  // Add new category
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Kategori adı boş olamaz');
      return;
    }

    setAddingCategory(true);
    try {
      await axios.post(`${API}/categories`, {
        name: newCategoryName.trim()
      });
      
      toast.success('Kategori başarıyla eklendi');
      setNewCategoryName('');
      setShowAddCategory(false);
      
      // Refresh categories
      await fetchCategories();
      
      // Auto-select the new category
      setFormData(prev => ({...prev, category: newCategoryName.trim()}));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kategori eklenirken hata oluştu');
    } finally {
      setAddingCategory(false);
    }
  };

  // Add new project
  const handleAddProject = async () => {
    if (!newProjectData.name.trim()) {
      toast.error('Proje adı boş olamaz');
      return;
    }

    setAddingProject(true);
    try {
      const response = await axios.post(`${API}/cost-centers/projects`, {
        project_name: newProjectData.name.trim()
      });
      
      toast.success('Proje başarıyla eklendi');
      const addedProjectName = newProjectData.name.trim();
      setNewProjectData({ name: '', description: '' });
      setShowAddProject(false);
      
      // Refresh projects
      await fetchProjects();
      
      // Auto-select the new project
      setFormData(prev => ({...prev, project: addedProjectName}));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Proje eklenirken hata oluştu');
    } finally {
      setAddingProject(false);
    }
  };

  // Auto-fill description as soon as category and project are picked.
  // Amount is optional — included once typed.
  const updateDescription = () => {
    if (!formData.category || !formData.project) return;
    const amountPart =
      formData.amount && parseFloat(formData.amount) > 0
        ? `${formData.amount} ${formData.currency} `
        : '';
    const autoDescription = `${formData.category} talebi - ${formData.project} projesi için ${amountPart}avans talebi. Proje ihtiyaçları doğrultusunda kullanılacak ve belirlenen sürede kapatılacaktır.`;
    setFormData((prev) => ({ ...prev, description: autoDescription }));
  };

  // Calculate closure date based on request date and selected days
  const calculateClosureDate = (days) => {
    if (!formData.request_date || !days) return '';
    
    const requestDate = new Date(formData.request_date);
    const closureDate = new Date(requestDate);
    closureDate.setDate(closureDate.getDate() + parseInt(days));
    
    return closureDate.toISOString().split('T')[0];
  };

  // Handle quick days selection
  const handleQuickDaysSelection = (days) => {
    if (!advanceRules) return;
    // Basit avans mode'unda kural doğrulama / hak kontrolü modalı açılmaz —
    // seçilen gün doğrudan forma uygulanır (kısıtsız).
    const simpleMode = user?.advance_mode === 'simple';

    // Check if this is medium days and rules are enabled
    if (!simpleMode && advanceRules.rules_enabled && days === advanceRules.medium_days.toString()) {
      setShowMediumModal(true);
      return;
    }

    // Check if this is extended days and rules are enabled
    if (!simpleMode && advanceRules.rules_enabled && days === advanceRules.extended_days.toString()) {
      setShowExtendedModal(true);
      return;
    }
    
    // For standard days or when rules are disabled
    const calculatedClosureDate = calculateClosureDate(days);
    setFormData(prev => ({
      ...prev,
      closure_type: 'days',
      closure_days: days,
      closure_date: calculatedClosureDate
    }));
  };

  // Handle medium days modal - show extended option
  const handleUseMediumDaysExtended = async () => {
    setLoadingAdvanceData(true);
    try {
      await axios.post(`${API}/advance-usage/use-extended`);
      
      // Use extended days (60)
      const calculatedClosureDate = calculateClosureDate(advanceRules.extended_days.toString());
      setFormData(prev => ({
        ...prev,
        closure_type: 'days',
        closure_days: advanceRules.extended_days.toString(),
        closure_date: calculatedClosureDate
      }));
      
      // Refresh usage data
      await fetchAdvanceRules();
      
      toast.success(`${advanceRules.extended_days} günlük hakkınız kullanıldı`);
      setShowMediumModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hak kullanımında hata oluştu');
    } finally {
      setLoadingAdvanceData(false);
    }
  };

  // Handle medium days modal - use standard
  const handleUseMediumDaysStandard = () => {
    const calculatedClosureDate = calculateClosureDate(advanceRules.standard_days.toString());
    setFormData(prev => ({
      ...prev,
      closure_type: 'days',
      closure_days: advanceRules.standard_days.toString(),
      closure_date: calculatedClosureDate
    }));
    setShowMediumModal(false);
  };

  // Handle medium days modal - use medium (30 days)
  const handleUseMediumDays = () => {
    const calculatedClosureDate = calculateClosureDate(advanceRules.medium_days.toString());
    setFormData(prev => ({
      ...prev,
      closure_type: 'days',
      closure_days: advanceRules.medium_days.toString(),
      closure_date: calculatedClosureDate
    }));
    setShowMediumModal(false);
  };

  // Handle extended days modal - use extended
  const handleUseExtendedDays = async () => {
    setLoadingAdvanceData(true);
    try {
      await axios.post(`${API}/advance-usage/use-extended`);
      
      // Use extended days
      const calculatedClosureDate = calculateClosureDate(advanceRules.extended_days.toString());
      setFormData(prev => ({
        ...prev,
        closure_type: 'days',
        closure_days: advanceRules.extended_days.toString(),
        closure_date: calculatedClosureDate
      }));
      
      // Refresh usage data
      await fetchAdvanceRules();
      
      toast.success(`${advanceRules.extended_days} günlük hakkınız kullanıldı`);
      setShowExtendedModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hak kullanımında hata oluştu');
    } finally {
      setLoadingAdvanceData(false);
    }
  };

  // Handle extended days modal - use standard
  const handleUseExtendedDaysStandard = () => {
    const calculatedClosureDate = calculateClosureDate(advanceRules.standard_days.toString());
    setFormData(prev => ({
      ...prev,
      closure_type: 'days',
      closure_days: advanceRules.standard_days.toString(),
      closure_date: calculatedClosureDate
    }));
    setShowExtendedModal(false);
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  React.useEffect(() => {
    updateDescription();
  }, [formData.amount, formData.category, formData.project]);

  // Update closure date when request date changes and days are selected
  React.useEffect(() => {
    if (formData.closure_type === 'days' && formData.closure_days && formData.request_date) {
      const calculatedDate = calculateClosureDate(formData.closure_days);
      setFormData(prev => ({ ...prev, closure_date: calculatedDate }));
    }
  }, [formData.request_date, formData.closure_days, formData.closure_type]);

  return (
    <div className="max-w-4xl mx-auto" data-testid="new-request-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3">
            <PlusIcon className="w-5 h-5 text-teal-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Yeni İş Avansı Talebi</h2>
        </div>
        <p className="text-gray-600">Lütfen talep bilgilerinizi eksiksiz doldurun</p>
      </div>

      {/* Eligibility Check */}
      {loadingEligibility ? (
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-center">
            <div className="loading-spinner mr-3"></div>
            <span>Avans uygunluk durumunuz kontrol ediliyor...</span>
          </div>
        </Card>
      ) : eligibility && (() => {
        // Basit avans mode'unda üst kısımdaki eligibility/limit özet kartı
        // gösterilmez — bu kart yalnızca gelişmiş modda anlamlıdır.
        if (user?.advance_mode === 'simple') {
          return null;
        }
        // KIRMIZI UYARI: Avans talebi oluşturamazsınız
        if (!eligibility.can_request) {
          return (
            <Card className="p-6 mb-6 border-2 border-red-200 bg-red-50" data-testid="eligibility-warning">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <XIcon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-900 mb-2">
                    Yeni Avans Talebi Oluşturamazsınız
                  </h3>
                  <p className="text-red-800 mb-4">
                    {eligibility.message}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="bg-white p-3 rounded-lg">
                      <div className="font-medium text-gray-900">Açık Avans Durumu</div>
                      <div className="text-gray-600">
                        {eligibility.open_advances_count} / {eligibility.max_open_advances} avans
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <div className="font-medium text-gray-900">Açık Avans Limiti</div>
                      <div className="text-gray-600">
                        {getCurrencySymbol(eligibility.base_currency)}
                        {(eligibility.total_amount_base || eligibility.total_amount_usd)?.toLocaleString('tr-TR')} / {getCurrencySymbol(eligibility.base_currency)}
                        {(eligibility.max_total_amount_base || eligibility.max_total_amount_usd)?.toLocaleString('tr-TR')} {eligibility.base_currency}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Kalan: {getCurrencySymbol(eligibility.base_currency)}
                        {((eligibility.max_total_amount_base || eligibility.max_total_amount_usd) - (eligibility.total_amount_base || eligibility.total_amount_usd))?.toLocaleString('tr-TR')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          );
        }
        
        // YEŞİL BAŞARI: Yeni avans talebi oluşturabilirsiniz
        return (
          <Card className="p-6 mb-6 border-2 border-green-200 bg-green-50" data-testid="eligibility-success">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckIcon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-green-900 mb-2">
                  Yeni Avans Talebi Oluşturabilirsiniz
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white p-3 rounded-lg">
                    <div className="font-medium text-gray-900">Açık Avans</div>
                    <div className="text-gray-600">
                      {eligibility.open_advances_count} / {eligibility.max_open_advances}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium text-gray-900 flex items-center">
                        Açık Avans Limiti
                        {!hideLimitInfo && (
                          <button
                            type="button"
                            onClick={() => setShowLimitInfoModal(true)}
                            data-testid="limit-info-icon-btn"
                            aria-label="Açık avans limiti hakkında bilgi"
                            className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors relative group"
                          >
                            <span className="absolute inset-0 rounded-full bg-teal-400 opacity-60 animate-ping pointer-events-none" />
                            <InfoIcon className="w-3 h-3 relative" />
                          </button>
                        )}
                      </div>
                      {hasAdminPermissions() && (
                        <button
                          type="button"
                          onClick={openLimitEditModal}
                          data-testid="limit-edit-link"
                          className="inline-flex items-center text-xs font-medium text-teal-600 hover:text-teal-700 hover:underline"
                        >
                          <Edit3Icon className="w-3 h-3 mr-1" />
                          Limit Değiştir
                        </button>
                      )}
                    </div>
                    <div className="text-gray-600">
                      {getCurrencySymbol(eligibility.base_currency)}
                      {(eligibility.total_amount_base || eligibility.total_amount_usd)?.toLocaleString('tr-TR')} / {getCurrencySymbol(eligibility.base_currency)}
                      {(eligibility.max_total_amount_base || eligibility.max_total_amount_usd)?.toLocaleString('tr-TR')} {eligibility.base_currency}
                    </div>
                    <div className="text-sm text-green-700 mt-1 font-medium">
                      Kalan: {getCurrencySymbol(eligibility.base_currency)}
                      {((eligibility.max_total_amount_base || eligibility.max_total_amount_usd) - (eligibility.total_amount_base || eligibility.total_amount_usd))?.toLocaleString('tr-TR')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        );
      })()}

      <Card className={`p-8 shadow-lg ${!eligibility?.can_request ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Talep Detayları</h3>
          <div className="w-16 h-1 bg-teal-500 rounded-full"></div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Amount and Currency */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="amount" className="form-label">Talep Tutarı *</Label>
              <AmountInput
                id="amount"
                value={formData.amount}
                onChange={(value) => setFormData({...formData, amount: value})}
                placeholder="0,00"
                required
                data-testid="request-amount-input"
              />
            </div>

            <div>
              <Label htmlFor="currency" className="form-label">Para Birimi *</Label>
              <Select value={formData.currency || undefined} onValueChange={(value) => setFormData({...formData, currency: value})}>
                <SelectTrigger data-testid="currency-select" className="form-input">
                  <SelectValue placeholder="Para birimi seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {loadingCurrencies ? (
                    <SelectItem value="TRY" disabled>Yükleniyor...</SelectItem>
                  ) : currencies.length > 0 ? (
                    (() => {
                      // Filter by user's allowed currencies from new API
                      const filteredCurrencies = userAllowedCurrencies.length > 0
                        ? currencies.filter(c => userAllowedCurrencies.includes(c.code))
                        : currencies;
                      
                      return filteredCurrencies.length > 0 ? (
                        filteredCurrencies.map((currency) => (
                          <SelectItem key={currency.code} value={currency.code}>
                            {currency.symbol} {currency.code}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="TRY" disabled>
                          Para birimi ayarlanmamış. Lütfen Ayarlar&apos;dan para birimi ekleyin.
                        </SelectItem>
                      );
                    })()
                  ) : (
                    (() => {
                      // Fallback currencies filtered by user settings
                      const fallbackCurrencies = [
                        { code: 'TRY', symbol: '₺' },
                        { code: 'USD', symbol: '$' },
                        { code: 'EUR', symbol: '€' }
                      ];
                      
                      const filteredFallback = userAllowedCurrencies.length > 0
                        ? fallbackCurrencies.filter(c => userAllowedCurrencies.includes(c.code))
                        : fallbackCurrencies;
                      
                      return filteredFallback.length > 0 ? (
                        filteredFallback.map((currency) => (
                          <SelectItem key={currency.code} value={currency.code}>
                            {currency.symbol} {currency.code}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="TRY" disabled>
                          Para birimi ayarlanmamış. Lütfen Ayarlar&apos;dan para birimi ekleyin.
                        </SelectItem>
                      );
                    })()
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Category */}
          <div>
            <Label htmlFor="category" className="form-label">Kategori *</Label>
            <Select value={formData.category} onValueChange={(value) => {
              if (value === 'add_new_category') {
                setShowAddCategory(true);
              } else {
                setFormData({...formData, category: value});
              }
            }}>
              <SelectTrigger data-testid="category-select" className="form-input">
                <SelectValue placeholder={loadingCategories ? "Kategoriler yükleniyor..." : "Kategori seçin"} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.name}>
                    <div className="flex items-center justify-between w-full">
                      <span>{getCategoryDisplayName(category)}</span>
                      {category.is_default && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          Varsayılan
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
                {hasAdminPermissions() && (
                  <>
                    <div className="border-t border-gray-200 my-1"></div>
                    <SelectItem value="add_new_category" className="text-teal-600 font-medium">
                      <div className="flex items-center">
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Yeni Kategori Ekle
                      </div>
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Add New Category Dialog */}
          {showAddCategory && (
            <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
              <DialogContent data-testid="add-category-dialog">
                <DialogHeader>
                  <DialogTitle>Yeni Kategori Ekle</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="new-category-name" className="form-label">
                      Kategori Adı *
                    </Label>
                    <Input
                      id="new-category-name"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="form-input"
                      placeholder="Örn: Eğitim Avansı"
                      maxLength={50}
                      data-testid="new-category-input"
                    />
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                    <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                      <CheckIcon className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-800 font-medium">Bilgilendirme</p>
                      <p className="text-sm text-blue-700">
                        Eklenen kategori tüm şirket kullanıcıları tarafından görülebilir olacaktır.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddCategory(false);
                        setNewCategoryName('');
                      }}
                      disabled={addingCategory}
                      className="flex-1"
                      data-testid="cancel-category-btn"
                    >
                      İptal
                    </Button>
                    <Button
                      onClick={handleAddCategory}
                      disabled={addingCategory || !newCategoryName.trim()}
                      className="btn-primary flex-1"
                      data-testid="save-category-btn"
                    >
                      {addingCategory ? (
                        <div className="loading-spinner mr-2"></div>
                      ) : (
                        <PlusIcon className="w-4 h-4 mr-2" />
                      )}
                      <span>{addingCategory ? 'Ekleniyor...' : 'Kategori Ekle'}</span>
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Add New Project Dialog */}
          {showAddProject && (
            <Dialog open={showAddProject} onOpenChange={setShowAddProject}>
              <DialogContent data-testid="add-project-dialog">
                <DialogHeader>
                  <DialogTitle>Yeni Proje Ekle</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="new-project-name" className="form-label">
                      Proje Adı *
                    </Label>
                    <Input
                      id="new-project-name"
                      value={newProjectData.name}
                      onChange={(e) => setNewProjectData(prev => ({...prev, name: e.target.value}))}
                      className="form-input"
                      placeholder="Örn: Web Geliştirme Projesi"
                      maxLength={100}
                      data-testid="new-project-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="new-project-description" className="form-label">
                      Açıklama (Opsiyonel)
                    </Label>
                    <textarea
                      id="new-project-description"
                      value={newProjectData.description}
                      onChange={(e) => setNewProjectData(prev => ({...prev, description: e.target.value}))}
                      className="form-input min-h-[80px] resize-none"
                      placeholder="Proje hakkında kısa açıklama..."
                      maxLength={200}
                      rows={3}
                      data-testid="new-project-description-input"
                    />
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                    <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                      <CheckIcon className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-800 font-medium">Bilgilendirme</p>
                      <p className="text-sm text-blue-700">
                        Eklenen proje tüm şirket kullanıcıları tarafından kullanılabilir olacaktır.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddProject(false);
                        setNewProjectData({ name: '', description: '' });
                      }}
                      disabled={addingProject}
                      className="flex-1"
                      data-testid="cancel-project-btn"
                    >
                      İptal
                    </Button>
                    <Button
                      onClick={handleAddProject}
                      disabled={addingProject || !newProjectData.name.trim()}
                      className="btn-primary flex-1"
                      data-testid="save-project-btn"
                    >
                      {addingProject ? (
                        <div className="loading-spinner mr-2"></div>
                      ) : (
                        <PlusIcon className="w-4 h-4 mr-2" />
                      )}
                      <span>{addingProject ? 'Ekleniyor...' : 'Proje Ekle'}</span>
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Project */}
          <div>
            <Label htmlFor="project" className="form-label">Proje/Saha Adı *</Label>
            <Select value={formData.project} onValueChange={(value) => {
              if (value === 'add_new_project') {
                setShowAddProject(true);
              } else {
                setFormData({...formData, project: value});
              }
            }}>
              <SelectTrigger data-testid="project-select" className="form-input">
                <SelectValue placeholder={loadingProjects ? "Projeler yükleniyor..." : "Proje seçin"} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.project_name || project.name}>
                    <div className="flex items-center justify-between w-full">
                      <div className="font-medium">{project.project_name || project.name}</div>
                      {project.is_default && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          Varsayılan
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
                {hasAdminPermissions() && (
                  <>
                    <div className="border-t border-gray-200 my-1"></div>
                    <SelectItem value="add_new_project" className="text-teal-600 font-medium">
                      <div className="flex items-center">
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Yeni Proje Ekle
                      </div>
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Medium Days Modal (30 gün) */}
          {showMediumModal && advanceRules && advanceUsage && (
            <Dialog open={showMediumModal} onOpenChange={setShowMediumModal}>
              <DialogContent data-testid="medium-days-modal" className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold text-center">
                    Orta Süre Seçeneği
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-sm text-blue-800 mb-2">
                      <strong>Standart kapama süresi {advanceRules.standard_days} gündür</strong>
                    </div>
                    <div className="text-sm text-blue-700">
                      {advanceRules.medium_days} gün için özel hakkınızı kullanabilirsiniz.
                    </div>
                  </div>

                  <div className="text-center p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="text-sm text-amber-800">
                      {advanceUsage.message}
                    </div>
                    <div className="text-sm text-amber-700 mt-2 font-medium">
                      {advanceRules.medium_days} gün hakkınızı kullanmak istiyor musunuz?
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={handleUseMediumDaysStandard}
                      disabled={loadingAdvanceData}
                      variant="outline"
                      className="flex-1"
                      data-testid="use-standard-from-medium-btn"
                    >
                      {advanceRules.standard_days} Gün (Standart)
                    </Button>
                    
                    <Button
                      onClick={handleUseMediumDays}
                      disabled={loadingAdvanceData}
                      className="btn-primary flex-1"
                      data-testid="use-medium-days-btn"
                    >
                      {advanceRules.medium_days} Gün Hakkını Kullan
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Extended Days Modal (60 gün) */}
          {showExtendedModal && advanceRules && advanceUsage && (
            <Dialog open={showExtendedModal} onOpenChange={setShowExtendedModal}>
              <DialogContent data-testid="extended-days-modal" className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold text-center">
                    Uzun Süre Seçeneği ({advanceRules.extended_days} Gün)
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-sm text-blue-800 mb-2">
                      <strong>Standart kapama süresi {advanceRules.standard_days} gündür</strong>
                    </div>
                    <div className="text-sm text-blue-700">
                      Sadece yoğun sezon için bir takvim yılında en fazla {advanceRules.yearly_extended_limit} defa {advanceRules.extended_days} gün seçeneği sunulur.
                    </div>
                  </div>

                  <div className="text-center p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="text-sm text-red-800 font-medium">
                      ⚠️ {advanceRules.extended_days} Gün Hakkı Kullanımı
                    </div>
                    <div className="text-sm text-red-700 mt-1">
                      {advanceUsage.message}
                    </div>
                    {advanceUsage.can_use_extended && (
                      <div className="text-sm text-red-700 mt-2 font-medium">
                        Bu hakkınızı kullanmak istiyor musunuz?
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    {advanceUsage.can_use_extended ? (
                      <>
                        <Button
                          variant="outline"
                          onClick={handleUseExtendedDaysStandard}
                          disabled={loadingAdvanceData}
                          className="flex-1"
                          data-testid="use-standard-from-extended-btn"
                        >
                          {advanceRules.standard_days} Gün (Standart)
                        </Button>
                        <Button
                          onClick={handleUseExtendedDays}
                          disabled={loadingAdvanceData}
                          className="btn-primary flex-1"
                          data-testid="use-extended-days-btn"
                        >
                          {loadingAdvanceData ? (
                            <div className="loading-spinner mr-2"></div>
                          ) : (
                            <CheckIcon className="w-4 h-4 mr-2" />
                          )}
                          <span>Hakkı Kullan ({advanceRules.extended_days} Gün)</span>
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={handleUseExtendedDaysStandard}
                        className="btn-primary w-full"
                        data-testid="only-standard-from-extended-btn"
                      >
                        Tamam ({advanceRules.standard_days} Gün)
                      </Button>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Description */}
          <div>
            <Label htmlFor="description" className="form-label">Açıklama *</Label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="form-input min-h-[120px] resize-none"
              placeholder="Kategori ve proje seçince otomatik doldurulur..."
              required
              rows={4}
              data-testid="description-input"
            />
          </div>

          {/* Request Date and Closure Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="request_date" className="form-label">Talep Tarihi *</Label>
              <Input
                id="request_date"
                type="date"
                value={formData.request_date}
                onChange={(e) => setFormData({...formData, request_date: e.target.value})}
                className="form-input modern-date-picker"
                required
                data-testid="request-date-input"
              />
            </div>

            <div>
              <Label className="form-label">Kapama Tarihi *</Label>
              
              {/* Quick Days Selection */}
              <div className="space-y-3">
                <div className="flex gap-2">
                  {advanceRules ? [
                    advanceRules.standard_days.toString(),
                    advanceRules.medium_days.toString(),
                    advanceRules.extended_days.toString()
                  ].map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => handleQuickDaysSelection(days)}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        formData.closure_type === 'days' && formData.closure_days === days
                          ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      } ${
                        // Disable extended days button if no rights left and rules enabled.
                        // Basit mode'da hak kavramı olmadığı için buton hep aktif.
                        user?.advance_mode !== 'simple' &&
                        advanceRules.rules_enabled && 
                        days === advanceRules.extended_days.toString() && 
                        advanceUsage && 
                        !advanceUsage.can_use_extended
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      disabled={
                        user?.advance_mode !== 'simple' &&
                        advanceRules.rules_enabled && 
                        days === advanceRules.extended_days.toString() && 
                        advanceUsage && 
                        !advanceUsage.can_use_extended
                      }
                      data-testid={`closure-${days}-days`}
                    >
                      {days} Gün
                      {/* Show rights info for medium and extended days when rules are enabled.
                          Basit avans mode'unda hak/yıl bilgisi anlamsız — gizli. */}
                      {advanceRules.rules_enabled && user?.advance_mode !== 'simple' && (
                        <>
                          {days === advanceRules.medium_days.toString() && (
                            <span className="text-xs block">
                              ({advanceRules.yearly_medium_limit} hak/yıl)
                            </span>
                          )}
                          {days === advanceRules.extended_days.toString() && (
                            <span className="text-xs block">
                              {advanceUsage && advanceUsage.can_use_extended ? 
                                `(${advanceUsage.remaining_extended} hak)` : 
                                '(Hak yok)'
                              }
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  )) : (
                    // Loading state or no rules
                    [15, 30, 60].map((days) => (
                      <button
                        key={days}
                        type="button"
                        disabled
                        className="px-4 py-2 rounded-lg border-2 border-gray-200 text-gray-400 cursor-not-allowed"
                      >
                        {days} Gün
                      </button>
                    ))
                  )}
                </div>
                
                {/* Calculated Date Display */}
                {formData.closure_type === 'days' && formData.closure_date && (
                  <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg" data-testid="calculated-closure-date">
                    <div className="flex items-center">
                      <CheckIcon className="w-4 h-4 text-green-500 mr-2" />
                      <span>
                        <strong>Kapama Tarihi:</strong> {formatDateForDisplay(formData.closure_date)} 
                        <span className="text-gray-500 ml-2">
                          ({formData.closure_days} gün sonra)
                        </span>
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Date Selection */}
              <div className="space-y-2">
                <Label className="text-sm text-gray-600">Veya özel tarih seçin:</Label>
                <Input
                  type="date"
                  value={formData.closure_date}
                  onChange={(e) => setFormData({...formData, closure_type: 'date', closure_date: e.target.value, closure_days: ''})}
                  className="form-input modern-date-picker"
                  placeholder="dd.mm.yyyy"
                  data-testid="closure-date-input"
                />
                
                {/* Manual Date Display */}
                {formData.closure_type === 'date' && formData.closure_date && (
                  <div className="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg" data-testid="manual-closure-date">
                    <div className="flex items-center">
                      <CheckIcon className="w-4 h-4 text-blue-500 mr-2" />
                      <span>
                        <strong>Seçilen Kapama Tarihi:</strong> {formatDateForDisplay(formData.closure_date)}
                        {formData.request_date && (() => {
                          const startDate = new Date(formData.request_date);
                          const endDate = new Date(formData.closure_date);
                          const diffTime = Math.abs(endDate - startDate);
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          return (
                            <span className="text-gray-500 ml-2">
                              ({diffDays} gün sonra)
                            </span>
                          );
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info Note */}
          <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
              <CheckIcon className="w-3 h-3 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-800 font-medium mb-1">Bilgilendirme</p>
              <p className="text-sm text-blue-700">
                Talebiniz oluşturulduktan sonra yöneticinize e-posta bildirimi gönderilecektir.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6 border-t border-gray-200">
            <Button
              type="submit"
              disabled={loading || !formData.amount || !formData.category || !formData.project || !formData.description || !formData.closure_days || !eligibility?.can_request}
              className="btn-primary w-full py-4 text-lg flex items-center justify-center"
              data-testid="submit-request-btn"
            >
              {loading ? (
                <div className="loading-spinner mr-2"></div>
              ) : (
                <PlusIcon className="w-5 h-5 mr-2" />
              )}
              <span>{loading ? 'Gönderiliyor...' : 'Talep Gönder'}</span>
            </Button>
          </div>
        </form>
      </Card>

      {/* Error Modal for Limit Exceeded */}
      <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-3 text-xl">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg 
                  className="w-6 h-6 text-red-600" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                  />
                </svg>
              </div>
              <span className="text-red-900">{errorModalData.title}</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Main Message */}
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
              <div className="flex items-start">
                <div className="ml-3">
                  <p className="text-sm text-red-800 leading-relaxed">
                    {errorModalData.message}
                  </p>
                </div>
              </div>
            </div>

            {/* Details Breakdown */}
            {errorModalData.details && Object.keys(errorModalData.details).length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 text-sm">Detaylı Bilgi:</h4>
                <div className="grid grid-cols-1 gap-3">
                  {errorModalData.details.requestedAmount && (
                    <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                      <span className="text-sm text-gray-600">Talep Edilen Miktar:</span>
                      <span className="text-sm font-semibold text-gray-900">{errorModalData.details.requestedAmount}</span>
                    </div>
                  )}
                  {errorModalData.details.currentOpen && (
                    <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                      <span className="text-sm text-gray-600">Mevcut Açık Avanslar:</span>
                      <span className="text-sm font-semibold text-gray-900">{errorModalData.details.currentOpen}</span>
                    </div>
                  )}
                  {errorModalData.details.maxLimit && (
                    <div className="flex justify-between items-center bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <span className="text-sm text-blue-700 font-medium">Maksimum İzin Verilen Limit:</span>
                      <span className="text-sm font-bold text-blue-900">{errorModalData.details.maxLimit}</span>
                    </div>
                  )}
                  {errorModalData.details.remainingLimit && (
                    <div className="flex justify-between items-center bg-green-50 p-3 rounded-lg border border-green-200">
                      <span className="text-sm text-green-700 font-medium">Kalan Kullanılabilir Limit:</span>
                      <span className="text-sm font-bold text-green-900">{errorModalData.details.remainingLimit}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Suggestion */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <svg 
                  className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Önerilen Çözüm:</p>
                  <ul className="list-disc list-inside space-y-1 ml-1">
                    <li>Talep miktarınızı kalan limit dahilinde düşürün</li>
                    <li>Veya mevcut açık avanslarınızdan birini kapatarak yeni limit oluşturun</li>
                    <li>Daha fazla bilgi için finans departmanı ile iletişime geçin</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowErrorModal(false)}
              className="px-6"
            >
              Kapat
            </Button>
            <Button
              onClick={() => {
                setShowErrorModal(false);
                // Focus on amount input
                document.querySelector('input[data-testid="request-amount-input"]')?.focus();
              }}
              className="px-6 bg-emerald-600 hover:bg-emerald-700"
            >
              Miktarı Düzenle
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Açık Avans Limiti — Info Modal */}
      <Dialog open={showLimitInfoModal} onOpenChange={setShowLimitInfoModal}>
        <DialogContent className="max-w-md" data-testid="limit-info-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center text-lg font-bold text-gray-900">
              <InfoIcon className="w-5 h-5 mr-2 text-teal-600" />
              Açık Avans Limiti Nedir?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>
              <strong>Açık Avans Limiti</strong>, henüz kapatılmamış (açık) tüm
              avans taleplerinizin toplam tutarına izin verilen üst sınırdır.
            </p>
            <ul className="list-disc list-inside space-y-1 ml-1">
              <li>Yeni bir talep oluşturduğunuzda, mevcut açık avanslarınızın toplamı bu limiti aşıyorsa talep oluşturulamaz.</li>
              <li>Bir avansı kapattığınızda o tutar tekrar limitinize eklenir.</li>
              <li>Bu sınırı yöneticiniz (Admin) “Limit Değiştir” bağlantısı ile düzenleyebilir.</li>
            </ul>
          </div>
          <div className="flex items-center justify-between pt-4 border-t mt-4">
            <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                data-testid="dont-show-again-checkbox"
              />
              <span>Bir daha gösterme</span>
            </label>
            <Button
              onClick={handleCloseLimitInfo}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="limit-info-close-btn"
            >
              Tamam
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Açık Avans Limiti — Edit Modal */}
      <Dialog open={showLimitEditModal} onOpenChange={setShowLimitEditModal}>
        <DialogContent className="max-w-md" data-testid="limit-edit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center text-lg font-bold text-gray-900">
              <Edit3Icon className="w-5 h-5 mr-2 text-teal-600" />
              Açık Avans Limiti Düzenle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="edit-max-open" className="form-label">
                Maksimum Açık Avans Sayısı
              </Label>
              <Input
                id="edit-max-open"
                type="number"
                min="1"
                value={limitForm.max_open_advances}
                onChange={(e) =>
                  setLimitForm({ ...limitForm, max_open_advances: e.target.value })
                }
                className="form-input"
                data-testid="limit-max-open-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-max-total" className="form-label">
                Maksimum Toplam Tutar ({eligibility?.base_currency || 'TRY'})
              </Label>
              <Input
                id="edit-max-total"
                type="number"
                min="0"
                step="0.01"
                value={limitForm.max_total_amount}
                onChange={(e) =>
                  setLimitForm({ ...limitForm, max_total_amount: e.target.value })
                }
                className="form-input"
                data-testid="limit-max-total-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-end space-x-3 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setShowLimitEditModal(false)}
              disabled={savingLimit}
              data-testid="limit-edit-cancel-btn"
            >
              İptal
            </Button>
            <Button
              onClick={handleSaveLimit}
              disabled={savingLimit}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="limit-edit-save-btn"
            >
              {savingLimit ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
